import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AGENTS, AGENT_IDS } from '../agents.js';
import AgentMark from '../components/AgentMark.jsx';
import {
  fetchRoundtables, fetchRoundtable, convene, conveneStream,
  fetchDisputes, ruleDispute,
  fetchAwayWork, setAwayWork, runAwayWork,
} from '../room-model.js';

export default function TheRoom({ projectId, onChanged }) {
  const [away, setAway] = useState(null);
  const [roundtables, setRoundtables] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [openRt, setOpenRt] = useState(null);
  const [topic, setTopic] = useState('');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const reload = useCallback(async () => {
    const [a, r, d] = await Promise.all([
      fetchAwayWork(projectId).catch(() => null),
      fetchRoundtables(projectId).catch(() => null),
      fetchDisputes(projectId).catch(() => null),
    ]);
    if (!alive.current) return;
    if (a) setAway(a);
    if (r) setRoundtables(r.roundtables || []);
    if (d) setDisputes(d.disputes || []);
    setLoaded(true);
    if (onChanged) onChanged();
  }, [projectId, onChanged]);

  useEffect(() => { reload(); }, [reload]);

  async function withBusy(fn) { setBusy(true); try { await fn(); } finally { await reload(); if (alive.current) setBusy(false); } }

  const toggleAway = () => withBusy(() => setAwayWork(projectId, !(away && away.enabled)));
  const runAway = () => withBusy(() => runAwayWork(projectId).catch(() => {}));
  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const doConvene = async () => {
    if (!topic.trim() || picked.length < 1) return;
    const sessionTopic = topic.trim();
    if (alive.current) setLiveSession({ topic: sessionTopic, turns: [] });
    if (alive.current) setBusy(true);
    try {
      await conveneStream(projectId, { topic: sessionTopic, participants: picked, goal: '', roundCap: 3 }, (e) => {
        if (!alive.current) return;
        if (e.type === 'turn') {
          setLiveSession(prev => prev ? { ...prev, turns: [...prev.turns, { agent: e.agent, contribution: e.contribution, stance: e.stance, round: e.round }] } : prev);
        } else if (e.type === 'done' || e.type === 'error') {
          setLiveSession(null);
          setTopic('');
          setPicked([]);
        }
      });
    } finally {
      if (alive.current) {
        setLiveSession(null);
        setTopic('');
        setPicked([]);
        await reload();
        setBusy(false);
      }
    }
  };
  const openTranscript = async (rt) => {
    if (openRt && openRt._id === rt._id) { setOpenRt(null); return; }
    const full = await fetchRoundtable(projectId, rt._id).catch(() => null);
    if (alive.current && full) setOpenRt(full);
  };
  const rule = (d, outcome) => {
    const note = window.prompt('Add a note for the room? (optional)') || '';
    return withBusy(() => ruleDispute(projectId, d._id, outcome, note));
  };

  if (!loaded) return null;

  return (
    <div className="room-page">
      <div className="rp-head"><span className="mono-label">THE ROOM</span><h1>Where the agents work</h1></div>

      <section className="rp-block">
        <div className="rp-h"><h2>Away-work</h2></div>
        <p className="rp-sub">Let the room develop the thinnest sections between your visits. Everything it makes lands as proposals on the Brief for your call.</p>
        <div className="rp-row">
          <button type="button" className={`btn small ${away && away.enabled ? 'accent' : ''}`} disabled={busy} onClick={toggleAway}>
            {away && away.enabled ? 'On' : 'Off'}
          </button>
          <button type="button" className="btn small" disabled={busy || !(away && away.enabled)} onClick={runAway}>Run a pass now</button>
          {away && <span className="rp-meta">{away.runsToday}/{away.dailyCap} runs today</span>}
        </div>
      </section>

      <section className="rp-block">
        <div className="rp-h"><h2>Convene a roundtable</h2></div>
        <input className="rp-input" placeholder="Topic, e.g. design the ten rooms" value={topic} onChange={e => setTopic(e.target.value)} />
        <div className="rp-agents">
          {AGENT_IDS.map(id => {
            const a = AGENTS[id];
            const on = picked.includes(id);
            return (
              <button type="button" key={id} className={`rp-agent ${on ? 'on' : ''}`} onClick={() => togglePick(id)}>
                <AgentMark agent={a} size="xs" /><span>{a.name}</span>
              </button>
            );
          })}
        </div>
        <button type="button" className="btn small accent" disabled={busy || !topic.trim() || picked.length < 1} onClick={doConvene}>Convene</button>
      </section>

      {liveSession && (
        <section className="rp-block">
          <div className="rp-h">
            <h2>Live session</h2>
            <span className="rt-status running mono-label">live</span>
          </div>
          <p className="rp-sub">{liveSession.topic}</p>
          <div className="rt-transcript">
            {liveSession.turns.map((t, i) => {
              const a = AGENTS[t.agent];
              return (
                <div className="rt-turn" key={i}>
                  <div className="rt-turn-h"><AgentMark agent={a} size="xs" /><span>{(a ? a.name : t.agent).toUpperCase()}</span><span className="rt-stance">{t.stance}</span></div>
                  <p>{t.contribution}</p>
                </div>
              );
            })}
            {liveSession.turns.length === 0 && <p className="rp-sub">Waiting for agents...</p>}
          </div>
        </section>
      )}

      <section className="rp-block">
        <div className="rp-h"><h2>Roundtables</h2><span className="bs-count">{roundtables.length}</span></div>
        {roundtables.length === 0 && <p className="rp-sub">No sessions yet. Convene one above, or hand off inside a thread.</p>}
        {roundtables.map(rt => (
          <div className="rt-row" key={rt._id}>
            <button type="button" className="rt-line" onClick={() => openTranscript(rt)}>
              <span className="rt-topic">{rt.topic}</span>
              <span className={`rt-status ${rt.status}`}>{rt.status}</span>
              <span className="rt-meta">{(rt.producedEntryIds || []).length} proposed · {rt.trigger}</span>
            </button>
            {openRt && openRt._id === rt._id && (
              <div className="rt-transcript">
                {(openRt.scratchpad || []).map((c, i) => {
                  const a = AGENTS[c.agent];
                  return (
                    <div className="rt-turn" key={i}>
                      <div className="rt-turn-h"><AgentMark agent={a} size="xs" /><span>{(a ? a.name : c.agent).toUpperCase()}</span><span className="rt-stance">{c.stance}</span></div>
                      <p>{c.text}</p>
                    </div>
                  );
                })}
                {(!openRt.scratchpad || openRt.scratchpad.length === 0) && <p className="rp-sub">No contributions recorded.</p>}
              </div>
            )}
          </div>
        ))}
      </section>

      <section className="rp-block">
        <div className="rp-h"><h2>Disputes to rule</h2><span className="bs-count">{disputes.length}</span></div>
        {disputes.length === 0 && <p className="rp-sub">Nothing contested. Agents challenge an entry or ask to reopen a settled one, and it lands here.</p>}
        {disputes.map(d => {
          const a = AGENTS[d.agent];
          const isChallenge = d.kind === 'challenge';
          return (
            <div className="dispute-card" key={d._id}>
              <div className="dc-tag">
                <span className={`mono-label ${isChallenge ? 'challenge' : 'reopen'}`}>{isChallenge ? 'CHALLENGE' : 'REOPEN'}</span>
                <AgentMark agent={a} size="xs" /><span className="dc-agent">{(a ? a.name : d.agent).toUpperCase()}</span>
              </div>
              {d.target && <div className="dc-target">On: {d.target.prose}</div>}
              <p className="dc-arg">{d.argument}</p>
              <div className="pending-actions">
                {isChallenge ? (
                  <>
                    <button type="button" className="btn small accent" disabled={busy} onClick={() => rule(d, 'won')}>Strike the entry</button>
                    <button type="button" className="btn small ghost" disabled={busy} onClick={() => rule(d, 'lost')}>Uphold the entry</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn small accent" disabled={busy} onClick={() => rule(d, 'granted')}>Grant reopen</button>
                    <button type="button" className="btn small ghost" disabled={busy} onClick={() => rule(d, 'denied')}>Deny</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
